import { Link } from "react-router-dom";
import {
  ArrowRight, Check, X, Search, Smartphone, Gauge, Mail, Target, Shield, Sparkles, Bot,
  MessageSquare, CalendarCheck, HelpCircle, Star, Rocket, Zap, DollarSign, Wrench, HeartHandshake,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LeadForm from "../components/LeadForm";
import ChatbotWidget from "../components/ChatbotWidget";
import ConceptCard from "../components/ConceptCard";
import { concepts } from "../data/concepts";

const failures = [
  "Outdated and difficult to navigate",
  "Slow to load on mobile devices",
  "Hard to find on Google",
  "Invisible to AI search tools like ChatGPT",
  "Designed to look good but not generate leads",
];

const includes = [
  { icon: Sparkles,   label: "Custom Design Tailored to Your Business" },
  { icon: Smartphone, label: "Mobile-Friendly Experience" },
  { icon: Bot,        label: "AI Search Optimization" },
  { icon: Search,     label: "Search Engine Optimization (SEO)" },
  { icon: Gauge,      label: "Fast Load Speeds" },
  { icon: Mail,       label: "Lead Capture Forms" },
  { icon: Target,     label: "Call-to-Action Strategy" },
  { icon: Shield,     label: "Secure Hosting & Maintenance Options" },
];

const conversions = [
  "More phone calls",
  "More form submissions",
  "More appointments",
  "Better customer experience",
  "Higher conversion rates",
];

const aiFeatures = [
  { icon: MessageSquare, title: "AI Chat Assistant",       text: "Greets visitors, answers questions, and routes hot leads — 24/7." },
  { icon: Target,        title: "AI Lead Qualification",   text: "Scores every inquiry so your team only spends time on real opportunities." },
  { icon: CalendarCheck, title: "AI Appointment Booking",  text: "Books calls on your calendar without back-and-forth emails." },
  { icon: HelpCircle,    title: "AI FAQ Assistant",        text: "Instantly answers the same 20 questions every customer asks." },
  { icon: Star,          title: "AI Review Collection",    text: "Auto-asks happy customers for reviews when they're most likely to leave one." },
];

const process = [
  { n: "01", title: "Discovery", text: "We learn about your business, goals, and customers." },
  { n: "02", title: "Design",    text: "We create a clean, modern website tailored to your brand." },
  { n: "03", title: "Build",     text: "We optimize your website for speed, mobile devices, SEO, and AI search." },
  { n: "04", title: "Launch",    text: "Your website goes live and starts working for your business." },
];

const whyUs = [
  { icon: Rocket,       label: "Ship in under 1 week" },
  { icon: Sparkles,     label: "Modern custom designs" },
  { icon: Bot,          label: "AI search focused strategy" },
  { icon: Zap,          label: "Built to generate leads" },
  { icon: DollarSign,   label: "Affordable pricing" },
  { icon: HeartHandshake, label: "Ongoing support available" },
];

const faqs = [
  { q: "What is AI search optimization?",
    a: "Traditional SEO targets Google rankings. AI search optimization makes sure ChatGPT, Claude, Gemini, and Grok recommend your business when their users ask for one — so you're found in both worlds." },
  { q: "Do I own the website after launch?",
    a: "Yes — 100%. Code, content, hosting access, every credential. No vendor lock-in, ever." },
  { q: "How long does a new website take?",
    a: "Most small business sites ship in under 1 week from kickoff to launch. Larger builds with funnels, customer portals, or AI features typically take 2–4 weeks." },
  { q: "Can you connect my CRM, email, and booking tools?",
    a: "Yes. HubSpot, Mailchimp, Klaviyo, GoHighLevel, ActiveCampaign, Calendly, Stripe — if it has an API, we'll wire it up cleanly." },
];

export default function Landing() {
  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <Navbar />

      {/* ───────────────── HERO ───────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grain"
             style={{ backgroundImage: "linear-gradient(180deg,#FDFBF7 0%,#FDFBF7 60%,#F5F3ED 100%)" }}/>
        <div className="absolute top-24 -right-24 w-[520px] h-[520px] rounded-full -z-10 opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.35), transparent 70%)" }}/>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="max-w-4xl fade-up">
            <p className="label-eyebrow">AI-Powered Solutions · Human Touch</p>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight mt-6 font-medium">
              Simple. Beautiful. <em className="text-[#C5A059] not-italic">High-converting</em> custom websites — built for small businesses.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#5C5C5C] max-w-2xl leading-relaxed">
              Designed for Google, ChatGPT, and AI search to help your business get found, generate more leads, and grow faster.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059] bg-[#FDFBF7]" data-testid="hero-speed-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] animate-pulse"/>
              <span className="text-[13px] font-medium tracking-wide text-[#1A1A1A]">
                Ship your new site in <span className="text-[#C5A059] font-semibold">under 1 week</span>
              </span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="hero-cta-review" className="btn-primary">Get a free website review <ArrowRight size={16}/></a>
              <a href="#contact" data-testid="hero-cta-call" className="btn-secondary">Schedule a call</a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── WHY MOST SITES FAIL (DARK) ───────────────── */}
      <section className="py-24 md:py-32 bg-[#0E0E0F] text-[#FDFBF7] relative overflow-hidden">
        <div className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full opacity-30"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.5), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="label-eyebrow" style={{color:"#C5A059"}}>The problem</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
              Why most small business websites fail.
            </h2>
            <p className="mt-6 text-[#D1CDC0] leading-relaxed text-lg max-w-md">
              Your website should be working for you 24/7. Unfortunately, most aren't.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-4">
              {failures.map((f, i) => (
                <li key={f} data-testid={`fail-${i}`} className={`flex items-start gap-4 p-5 rounded-2xl border border-[#2a2a2c] bg-[#161618] fade-up delay-${i+1}`}>
                  <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#1F1F22] text-[#C5A059] border border-[#3a3a3c]">
                    <X size={16}/>
                  </span>
                  <span className="text-[17px] leading-relaxed text-[#FDFBF7]">{f}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-[#D1CDC0] text-lg leading-relaxed max-w-2xl">
              A great website should do more than exist — it should help your business grow.
            </p>
          </div>
        </div>
      </section>

      {/* ───────────────── BUILT FOR THE FUTURE OF SEARCH ───────────────── */}
      <section id="services" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-6">
              <p className="label-eyebrow">Built for what's next</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
                Websites built for the future of search.
              </h2>
            </div>
            <div className="md:col-span-6">
              <p className="text-lg text-[#5C5C5C] leading-relaxed">
                More people are turning to AI tools like ChatGPT, Gemini, and Claude to find local businesses, services, and recommendations.
              </p>
              <p className="mt-4 text-lg text-[#5C5C5C] leading-relaxed">
                We build websites designed to be understood by both search engines and AI platforms — so your business stays visible as search continues to evolve.
              </p>
            </div>
          </div>

          <p className="label-eyebrow mb-6">Every Website Includes</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {includes.map((it, i) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.label}
                  data-testid={`include-${i}`}
                  className={`group flex items-center gap-3 rounded-xl border border-[#EBE8DF] bg-white px-4 py-4 hover:border-[#C5A059] hover:shadow-[0_8px_22px_-12px_rgba(197,160,89,0.45)] transition-all fade-up delay-${(i%6)+1}`}
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FDFBF7] border border-[#EBE8DF] text-[#C5A059] shrink-0 group-hover:bg-[#C5A059] group-hover:text-white group-hover:border-[#C5A059] transition-colors">
                    <Icon size={16} strokeWidth={1.8}/>
                  </span>
                  <span className="text-[14px] sm:text-[15px] leading-snug font-medium text-[#1A1A1A]">
                    {it.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────────── TURN MORE VISITORS INTO CUSTOMERS (cream) ───────────────── */}
      <section className="py-24 md:py-32 bg-[#F5F3ED]/60 border-y border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <p className="label-eyebrow">Conversion</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
              Turn more visitors into customers.
            </h2>
            <p className="mt-6 text-lg text-[#5C5C5C] leading-relaxed max-w-md">
              A beautiful website is only valuable if it generates results. Every page we build is designed with one goal in mind — helping your business grow.
            </p>
          </div>
          <div className="md:col-span-6">
            <div className="rounded-2xl border border-[#EBE8DF] bg-white divide-y divide-[#EBE8DF]">
              {conversions.map((c, i) => (
                <div key={c} data-testid={`conv-${i}`} className="flex items-center gap-4 px-7 py-5">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C5A059] text-white">
                    <Check size={16}/>
                  </span>
                  <span className="text-[17px]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── INDUSTRY WEBSITE CONCEPTS ───────────────── */}
      <section id="concepts" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <p className="label-eyebrow">Portfolio · concepts</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
                Website designs built for <em className="text-[#C5A059] not-italic">your</em> industry.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-lg text-[#5C5C5C] leading-relaxed">
                Explore sample website concepts designed for service-based businesses. Every website is custom built and optimized for lead generation, Google, and AI search.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {concepts.map((c, i) => (
              <ConceptCard key={c.slug} concept={c} index={i}/>
            ))}
          </div>

          {/* Don't see your industry CTA */}
          <div className="mt-16 rounded-2xl border border-[#EBE8DF] bg-white p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center"
               data-testid="concepts-custom-cta">
            <div className="md:col-span-8">
              <p className="label-eyebrow">Don't see your industry?</p>
              <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
                We can create a custom website concept tailored specifically to your business.
              </h3>
              <p className="mt-4 text-[#5C5C5C] leading-relaxed max-w-xl">
                Tell us what you do — we'll design a free, no-obligation concept that shows what your site could look like.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a href="#contact" data-testid="concepts-custom-cta-button" className="btn-gold">
                Request a free website concept <ArrowRight size={16}/>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── AI SEARCH SPOTLIGHT CTA ───────────────── */}
      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl border-2 border-[#C5A059] bg-[#FDFBF7] p-10 md:p-14 grid md:grid-cols-12 gap-10 items-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full opacity-25"
                 style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.7), transparent 70%)" }}/>
            <div className="md:col-span-7 relative">
              <p className="label-eyebrow" style={{color:"#C5A059"}}>The small-business leapfrog</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 font-medium leading-tight">
                Be the business AI <em className="text-[#C5A059] not-italic">recommends</em>.
              </h2>
              <p className="mt-5 text-lg text-[#5C5C5C] leading-relaxed">
                Customers are asking ChatGPT, Claude, Gemini and Grok for recommendations — and the dominant brands aren't ready. Pre-templated sites aren't either. Our websites are custom-coded so AI engines can crawl your site, understand your offer, and recommend you to the buyer.
              </p>
            </div>
            <div className="md:col-span-5 relative flex md:flex-col gap-3">
              <Link to="/ai-search" data-testid="home-cta-ai" className="btn-gold w-full md:w-auto justify-center">
                Read: Future of AI Search <ArrowRight size={16}/>
              </Link>
              <a href="#concepts" data-testid="home-cta-examples" className="btn-secondary w-full md:w-auto justify-center">
                See example sites
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── OPTIONAL AI FEATURES (DARK) ───────────────── */}
      <section className="py-24 md:py-32 bg-[#0E0E0F] text-[#FDFBF7] relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full opacity-30"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.55), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <p className="label-eyebrow" style={{color:"#C5A059"}}>Optional add-ons</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
                Take it further with AI-powered tools.
              </h2>
            </div>
            <div className="md:col-span-6">
              <p className="text-lg text-[#D1CDC0] leading-relaxed">
                Let AI help answer questions, capture leads, and support customers around the clock — so you can focus on running your business.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {aiFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.title} data-testid={`ai-feature-${i}`}
                     className={`p-7 rounded-2xl border border-[#2a2a2c] bg-[#161618] hover:border-[#C5A059] transition-colors fade-up delay-${(i%6)+1}`}>
                  <Icon size={22} className="text-[#C5A059]" strokeWidth={1.6}/>
                  <h3 className="font-display text-2xl mt-5 leading-tight text-[#FDFBF7]">{f.title}</h3>
                  <p className="mt-2 text-[15px] text-[#A8A6A0] leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────────── PROCESS ───────────────── */}
      <section id="process" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-7">
              <p className="label-eyebrow">Process</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
                Calm. Transparent. Fast.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#5C5C5C] leading-relaxed">
                Four clear stages from kickoff to launch — with status visible in your private customer portal the whole way.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-px bg-[#EBE8DF] border border-[#EBE8DF] rounded-2xl overflow-hidden">
            {process.map((p, i) => (
              <div key={p.n} data-testid={`process-${i}`} className={`bg-[#FDFBF7] p-8 md:p-10 fade-up delay-${i+1}`}>
                <p className="font-mono text-[13px] text-[#C5A059]">{p.n}</p>
                <h3 className="font-display text-2xl mt-4">{p.title}</h3>
                <p className="mt-3 text-[15px] text-[#5C5C5C] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── WHY CHOOSE US (cream) ───────────────── */}
      <section id="why" className="py-24 md:py-32 bg-[#F5F3ED]/60 border-y border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <p className="label-eyebrow">Why us</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">
                Why businesses choose Shumway Tech.
              </h2>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyUs.map((w, i) => {
              const Icon = w.icon;
              return (
                <div key={w.label} data-testid={`why-${i}`} className={`flex items-center gap-4 px-6 py-5 rounded-2xl border border-[#EBE8DF] bg-white fade-up delay-${(i%6)+1}`}>
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FDFBF7] border border-[#EBE8DF] text-[#C5A059]">
                    <Icon size={18} strokeWidth={1.8}/>
                  </span>
                  <span className="font-display text-xl">{w.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────────── FAQ ───────────────── */}
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow">FAQ</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-4 font-medium">Questions, answered.</h2>
          <div className="mt-12 divide-y divide-[#EBE8DF] border-y border-[#EBE8DF]">
            {faqs.map((f, i) => (
              <details key={i} className="group py-6">
                <summary className="cursor-pointer list-none flex justify-between items-center font-display text-xl md:text-2xl">
                  {f.q}
                  <span className="text-[#C5A059] text-3xl font-light group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#5C5C5C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── FINAL CTA + CONTACT (DARK + form on light card) ───────────────── */}
      <section id="contact" className="relative">
        <div className="bg-[#0E0E0F] text-[#FDFBF7] pt-24 md:pt-32 pb-44 md:pb-64 relative overflow-hidden">
          <div className="absolute -top-32 right-1/4 w-[520px] h-[520px] rounded-full opacity-30"
               style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.55), transparent 70%)" }}/>
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
            <p className="label-eyebrow" style={{color:"#C5A059"}}>Let's build it</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4 font-medium">
              Ready for a <em className="text-[#C5A059] not-italic">better</em> website?
            </h2>
            <p className="mt-6 text-lg md:text-xl text-[#D1CDC0] leading-relaxed max-w-2xl mx-auto">
              Get a website that looks great, ranks better, and helps customers find your business through Google and AI search.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a href="#contact-form" data-testid="footer-cta-consult" className="btn-gold">Schedule a free consultation <ArrowRight size={16}/></a>
              <a href="#contact-form" data-testid="footer-cta-review"
                 className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#3a3a3c] text-[#FDFBF7] hover:bg-[#161618] transition-colors text-[14px] tracking-wide">
                Get your free website review
              </a>
            </div>
          </div>
        </div>

        {/* Form floating up over the dark band */}
        <div id="contact-form" className="max-w-3xl mx-auto px-6 md:px-12 -mt-32 md:-mt-44 relative pb-24">
          <LeadForm />
          <p className="text-center mt-6 text-sm text-[#5C5C5C]">
            Prefer email? <a href="mailto:hello@shumwaytech.com" className="ulink">hello@shumwaytech.com</a>
          </p>
        </div>
      </section>

      <Footer />
      <ChatbotWidget />
    </div>
  );
}
